import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import useNavigate from "../../../hooks/useNavigate";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import { InlineLoading, Modal, NotificationAlert } from "../../../components/common";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import { ModalSize } from "../../../enumerations/ModalSize";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms";
import * as PersonnelTrainingActions from "../../../actions/personnelTraining";
import * as PersonnelActions from "../../../actions/personnel";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { ITmdState } from "../../../reducers/index";
import {
    DatePickerFormInputFabric,
    SelectFormInput,
    TextAreaFormInput,
    TextFormInput,
} from "../../../components/form";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "../../../helpers/lookupHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { isUrl } from "../../../functions/string";

import "./PersonnelTrainingCertificationPage.scss";

const reduxFormName: string = "reduxForms.personnelTraining";

interface PersonnelTrainingFormModalProps {
    form: Dtos.PersonnelTraining & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    lookups: Dtos.Lookup[];
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    loadingPersonnelTraining: boolean;
    loadPersonnelTrainingSuccess: boolean;
    loadPersonnelTrainingFailure: boolean;
    loadPersonnelTrainingErrorCode: string;
    savingPersonnelTraining: boolean;
    savePersonnelTrainingSuccess: boolean;
    savePersonnelTrainingFailure: boolean;
    creatingPersonnelTraining: boolean;
    validatingPersonnelTraining: boolean;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    clearPersonnelTraining: PersonnelTrainingActions.IPersonnelTrainingClearActionCreator;
    loadPersonnelTraining: PersonnelTrainingActions.IPersonnelTrainingLoadByIdActionCreator;
    savePersonnelTraining: PersonnelTrainingActions.IPersonnelTrainingSaveActionCreator;
    createPersonnelTraining: PersonnelTrainingActions.IPersonnelTrainingCreateActionCreator;
    validatePersonnelTraining: PersonnelTrainingActions.IPersonnelTrainingValidateActionCreator;
    clearPersonnel: PersonnelActions.IPersonnelClearActionCreator;
    personnelTrainingId: number | null;
    personnelTraining: Dtos.PersonnelTraining;
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const PersonnelTrainingFormModal: FC<PersonnelTrainingFormModalProps> = ({
    form,
    formState,
    formProperties,
    lookups,
    validationFailures,
    permissions,
    loadingPersonnelTraining,
    loadPersonnelTrainingFailure,
    loadPersonnelTrainingErrorCode,
    savingPersonnelTraining,
    savePersonnelTrainingSuccess,
    savePersonnelTrainingFailure,
    creatingPersonnelTraining,
    validatingPersonnelTraining,
    loadForm,
    changeForm,
    resetForm,
    clearPersonnelTraining,
    loadPersonnelTraining,
    savePersonnelTraining,
    createPersonnelTraining,
    validatePersonnelTraining,
    clearPersonnel,
    personnelTrainingId,
    personnelTraining,
    isOpen,
    setIsOpen,
}) => {
    //
    // -- Params --
    const { personnelId } = useParams<{ personnelId: string }>();
    const personnelIdNumber = parseInt(personnelId, 10);

    //
    // -- States --
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    const loadReduxForm = () => {
        let extraData: Dtos.PersonnelTraining = {} as Dtos.PersonnelTraining;

        loadForm(
            reduxFormName,
            Object.assign({ ...personnelTraining }, extraData)
        );
    };

    // Load personnel training
    useEffect(() => {
        if (isOpen) {
            if (personnelTrainingId && !personnelTraining && !loadingPersonnelTraining) {
                loadPersonnelTraining(personnelTrainingId);
            } else if (personnelTrainingId == null && !personnelTraining && !creatingPersonnelTraining) {
                createPersonnelTraining(personnelIdNumber);
            } else if (personnelTraining && !formInitalised) {
                loadReduxForm();
                setFormInitalised(true);
            }
        }
    }, [
        personnelTrainingId,
        personnelTraining,
        creatingPersonnelTraining,
        loadingPersonnelTraining,
        formInitalised,
        isOpen
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadPersonnelTrainingFailure) {
            navigate(`/error/${loadPersonnelTrainingErrorCode}`);
        }
    }, [
        loadPersonnelTrainingFailure,
        loadPersonnelTrainingErrorCode,
        navigate,
    ]);

    // Cleanup effect to clear the personnel training on component unmount
    useEffect(() => {
        return () => {
            clearPersonnelTraining();
        };
    }, [clearPersonnelTraining]);

    // Reload personnel training after saving
    useEffect(() => {
        if (!savingPersonnelTraining && savePersonnelTrainingSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Personnel training saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                setIsOpen(false);
                clearPersonnelTraining();
                clearPersonnel();
            } else if (!personnelIdNumber) {
                clearPersonnelTraining();
                clearPersonnel();
            }
        }
    }, [
        saveAndReturn,
        savingPersonnelTraining,
        savePersonnelTrainingSuccess,
        personnelIdNumber,
        clearPersonnelTraining,
        clearPersonnel,
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingPersonnelTraining && savePersonnelTrainingFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingPersonnelTraining, savePersonnelTrainingFailure]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.PersonnelTraining = {
                personnelId: personnelIdNumber,
            } as Dtos.PersonnelTraining;

            await savePersonnelTraining(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.PersonnelTraining) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        setIsOpen(false);
        resetForm(reduxFormName);
        clearPersonnel();
        clearPersonnelTraining();
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.PersonnelTraining = {} as Dtos.PersonnelTraining;

            if (form && hasBeenSubmitted && !validatingPersonnelTraining) {
                await validatePersonnelTraining(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const openFile = (link: string) => {
        window.open(`${link}`, "_blank");
        return null;
    };

    const renderPersonnelTrainingForm = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.TrainingCertificationsUpdate,
            undefined,
            undefined
        );

        if (hasPermission) {
            const formDisabled: boolean =
                !hasPermission ||
                savingPersonnelTraining ||
                (formState && formState.formStatus == Dtos.FormStatus.Disabled);

            let formComponents: { [index: string]: RegistrationFormComponent } =
                {
                    details: {
                        type: RegistrationComponent.RegistrationElement,
                        component: (
                            <>
                                <h1 className="pl-4 pt-3">
                                    Personnel Training Details
                                </h1>
                                <div className="form-row pt-4 pb-2 px-4">
                                    <div className="col-12">
                                        {renderFormControls(
                                            form,
                                            "personnelTraining",
                                            {
                                                trainingTypeId: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    size: 2,
                                                    inputType: SelectFormInput,
                                                    inputDisabled: formDisabled,
                                                    inputProps: {
                                                        options:
                                                            generateOptionsFromLookup(
                                                                findLookup(
                                                                    "personnelTraining",
                                                                    "trainingTypeId",
                                                                    lookups
                                                                ),
                                                                form.trainingTypeId
                                                            ),
                                                        clearable: true,
                                                    },
                                                    groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                    labelClassName: "col-form-label col-2",
                                                    fieldClassName: "col-10 generic-form-field mr-0 pr-0",
                                                },
                                            },
                                            lookups,
                                            validationFailures,
                                            formProperties,
                                            changeForm
                                        )}
                                    </div>
                                </div>
                                <div className="form-row py-2 px-4">
                                    <div className="col-12">
                                        {renderFormControls(
                                            form,
                                            "personnelTraining",
                                            {
                                                trainingGeneralSystemTypeId: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    size: 2,
                                                    inputType: SelectFormInput,
                                                    inputDisabled: formDisabled,
                                                    inputProps: {
                                                        options:
                                                            generateOptionsFromLookup(
                                                                findLookup(
                                                                    "personnelTraining",
                                                                    "trainingGeneralSystemTypeId",
                                                                    lookups
                                                                ),
                                                                form.trainingGeneralSystemTypeId
                                                            ),
                                                        clearable: true,
                                                    },
                                                    groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                    labelClassName: "col-form-label col-2",
                                                    fieldClassName: "col-10 generic-form-field mr-0 pr-0",
                                                },
                                            },
                                            lookups,
                                            validationFailures,
                                            formProperties,
                                            changeForm
                                        )}
                                    </div>
                                </div>
                                <div className="form-row py-2 px-4">
                                    <div className="col-12">
                                        {renderFormControls(
                                            form,
                                            "personnelTraining",
                                            {
                                                certificateNumber: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    inputType: TextFormInput,
                                                    inputDisabled: formDisabled,
                                                    groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                    labelClassName: "col-form-label col-2",
                                                    fieldClassName: "col-10 generic-form-field mr-0 pr-0",
                                                },
                                            },
                                            lookups,
                                            validationFailures,
                                            formProperties,
                                            changeForm
                                        )}
                                    </div>
                                </div>
                                <div className="form-row py-2 px-4">
                                    <div className="col-12">
                                        {renderFormControls(
                                            form,
                                            "personnelTraining",
                                            {
                                                courseModule: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    inputType: TextAreaFormInput,
                                                    inputDisabled: formDisabled,
                                                    inputProps: {
                                                        rows: 3,
                                                    },
                                                    groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                    labelClassName: "col-form-label col-2",
                                                    fieldClassName: "col-10 generic-form-field mr-0 pr-0",
                                                },
                                            },
                                            lookups,
                                            validationFailures,
                                            formProperties,
                                            changeForm
                                        )}
                                    </div>
                                </div>
                                <div className="form-row py-2 px-4">
                                    <div className="col-12">
                                        {renderFormControls(
                                            form,
                                            "personnelTraining",
                                            {
                                                provider: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    inputType: TextFormInput,
                                                    inputDisabled: formDisabled,
                                                    groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                    labelClassName: "col-form-label col-2",
                                                    fieldClassName: "col-10 generic-form-field mr-0 pr-0",
                                                },
                                            },
                                            lookups,
                                            validationFailures,
                                            formProperties,
                                            changeForm
                                        )}
                                    </div>
                                </div>
                                <div className="form-row py-2 px-4">
                                    <div className="col-12">
                                        {renderFormControls(
                                            form,
                                            "personnelTraining",
                                            {
                                                dateCompleted: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    inputType: DatePickerFormInputFabric,
                                                    inputDisabled: formDisabled,
                                                    groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                    labelClassName: "col-form-label col-2",
                                                    fieldClassName: "col-10 generic-form-field mr-0 pr-0",
                                                },
                                            },
                                            lookups,
                                            validationFailures,
                                            formProperties,
                                            changeForm
                                        )}
                                    </div>
                                </div>
                                <div className="form-row py-2 px-4">
                                    <div className="col-12">
                                        {renderFormControls(
                                            form,
                                            "personnelTraining",
                                            {
                                                certificate: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    inputType: TextFormInput,
                                                    inputDisabled: formDisabled,
                                                    inputProps: {
                                                        addOnRightButton: (
                                                            <button
                                                                className="btn btn-secondary"
                                                                id="button-addon2"
                                                                type="button"
                                                                style={{
                                                                    padding: "4px 10px",
                                                                    cursor: form?.certificate == null || !isUrl(form?.certificate)
                                                                            ? "not-allowed"
                                                                            : "pointer",
                                                                }}
                                                                disabled={form?.certificate == null || !isUrl(form?.certificate)}
                                                                onClick={() => {
                                                                    openFile(form.certificate);
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.ARROWUPRIGHTFROMSQUARE} />
                                                            </button>
                                                        ),
                                                    },
                                                    groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                    labelClassName: "col-form-label col-2",
                                                    fieldClassName: "col-10 generic-form-field mr-0 pr-0",
                                                },
                                            },
                                            lookups,
                                            validationFailures,
                                            formProperties,
                                            changeForm
                                        )}
                                    </div>
                                </div>
                                <div className="form-row py-2 px-4">
                                    <div className="col-12">
                                        {renderFormControls(
                                            form,
                                            "personnelTraining",
                                            {
                                                additionalCertificate: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    inputType: TextFormInput,
                                                    inputDisabled: formDisabled,
                                                    inputProps: {
                                                        addOnRightButton: (
                                                            <button
                                                                className="btn btn-secondary"
                                                                id="button-addon2"
                                                                type="button"
                                                                style={{
                                                                    padding: "4px 10px",
                                                                    cursor: form?.additionalCertificate == null || !isUrl(form?.additionalCertificate)
                                                                            ? "not-allowed"
                                                                            : "pointer",
                                                                }}
                                                                disabled={form?.additionalCertificate == null || !isUrl(form?.additionalCertificate)}
                                                                onClick={() => {
                                                                    openFile(form.additionalCertificate);
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.ARROWUPRIGHTFROMSQUARE} />
                                                            </button>
                                                        ),
                                                    },
                                                    groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                    labelClassName: "col-form-label col-2",
                                                    fieldClassName: "col-10 generic-form-field mr-0 pr-0",
                                                },
                                            },
                                            lookups,
                                            validationFailures,
                                            formProperties,
                                            changeForm
                                        )}
                                    </div>
                                </div>
                                <div className="form-row pt-2 pb-4 px-4">
                                    <div className="col-12">
                                        {renderFormControls(
                                            form,
                                            "personnelTraining",
                                            {
                                                comments: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    inputType: TextAreaFormInput,
                                                    inputDisabled: formDisabled,
                                                    inputProps: {
                                                        rows: 3,
                                                    },
                                                    groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                    labelClassName: "col-form-label col-2",
                                                    fieldClassName: "col-10 generic-form-field mr-0 pr-0",
                                                },
                                            },
                                            lookups,
                                            validationFailures,
                                            formProperties,
                                            changeForm
                                        )}
                                    </div>
                                </div>
                            </>
                        ),
                    },
                };

            return (
                <Modal
                    open={isOpen}
                    disableCloseOnOverlayClick={true}
                    onClose={() => {
                        onCancel();
                    }}
                    size={ModalSize.Xl}
                >
                    <div className="modal-body" style={{ height: "742px" }}>
                        {loadingPersonnelTraining || creatingPersonnelTraining ||
                        !formInitalised ? (
                            <div className="d-flex h-100 w-100 align-items-center justify-content-center py-4">
                                <InlineLoading />
                            </div>
                        ) : (
                            <Form
                                model={reduxFormName}
                                onSubmit={(val, _event) => handleOnSubmit(val)}
                                onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                            >
                                {renderFormControls(
                                    form,
                                    "personnelTraining",
                                    formComponents,
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                                <div className="mt-4 mb-4 text-right">
                                    <button
                                        disabled={formDisabled}
                                        onClick={() => {
                                            changeForm(reduxFormName + ".saveAndReturn", false);
                                        }}
                                        type="submit"
                                        className="btn btn-primary mr-2"
                                    >
                                        Submit
                                    </button>
                                    <button
                                        disabled={formDisabled}
                                        onClick={() => {
                                            changeForm(reduxFormName + ".saveAndReturn", true);
                                        }}
                                        type="submit"
                                        className="btn btn-primary mr-2"
                                    >
                                        Submit And Return
                                    </button>
                                    <button
                                        onClick={onCancel}
                                        className="btn btn-primary mr-2"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </Form>
                        )}
                    </div>
                </Modal>
            );
        }
        return null;
    }, [
        isOpen,
        permissions,
        lookups,
        form,
        permissions,
        onCancel,
        handleOnSubmit,
        handleOnSubmitFailed,
        loadingPersonnelTraining,
        savingPersonnelTraining,
        creatingPersonnelTraining,
        formState,
    ]);

    return <>{renderPersonnelTrainingForm}</>;
};

const mapStateToProps = (
    state: ITmdState,
    ownProps: Partial<PersonnelTrainingFormModalProps>
): Partial<PersonnelTrainingFormModalProps> => {
    let personnelTraining: Dtos.PersonnelTraining | undefined = !(
        state.personnelTraining.formData instanceof Array
    )
        ? state.personnelTraining.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        personnelTraining: personnelTraining!,
        loadingPersonnelTraining: state.personnelTraining.loadState && state.personnelTraining.loadState.status == RequestState.Pending,
        loadPersonnelTrainingSuccess: state.personnelTraining.loadState && state.personnelTraining.loadState.status == RequestState.Success,
        loadPersonnelTrainingFailure: state.personnelTraining.loadState && state.personnelTraining.loadState.status == RequestState.Failure,
        loadPersonnelTrainingErrorCode: state.personnelTraining.loadState.errorCode,
        savingPersonnelTraining: state.personnelTraining.saveState && state.personnelTraining.saveState.status == RequestState.Pending,
        savePersonnelTrainingSuccess: state.personnelTraining.saveState && state.personnelTraining.saveState.status == RequestState.Success,
        savePersonnelTrainingFailure: state.personnelTraining.saveState && state.personnelTraining.saveState.status == RequestState.Failure,
        creatingPersonnelTraining: state.personnelTraining.createState && state.personnelTraining.createState.status == RequestState.Pending,
        validatingPersonnelTraining: state.personnelTraining.validateState && state.personnelTraining.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: state.personnelTraining.lookups,
        form: state.reduxForms.personnelTraining,
        formState: state.personnelTraining.formState,
        formProperties: state.personnelTraining.formProperties,
        validationFailures: personnelTraining
            ? state.personnelTraining.validationFailures
            : [],
        personnelTrainingId: ownProps.personnelTrainingId,
        isOpen: ownProps.isOpen,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<PersonnelTrainingFormModalProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadPersonnelTraining: bindActionCreators(PersonnelTrainingActions.LoadPersonnelTrainingById, dispatch),
        clearPersonnelTraining: bindActionCreators(PersonnelTrainingActions.Clear, dispatch),
        savePersonnelTraining: bindActionCreators(PersonnelTrainingActions.SavePersonnelTraining, dispatch),
        createPersonnelTraining: bindActionCreators(PersonnelTrainingActions.CreatePersonnelTraining, dispatch),
        validatePersonnelTraining: bindActionCreators(PersonnelTrainingActions.ValidatePersonnelTraining, dispatch),
        clearPersonnel: bindActionCreators(PersonnelActions.Clear, dispatch),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(PersonnelTrainingFormModal);
